import styled from '@emotion/styled'
import { Gray100, Gray200, White } from '@mehilainen/mds-customer/colors'
import { typography, spacing } from '@mehilainen/mds-tokens'
import { Button, Stack } from '@mui/material'
import { TFunction } from 'i18next'
import React, { useRef, useState } from 'react'

import { AppointmentPrice } from '../../../__generated__/api'
import Modal from '../../../common/components/Modal/Modal'
import { Text } from '../../../common/components/Typography/Typography'
import { useIsMobile } from '../../../common/hooks/useBreakpoint'
import useTranslation from '../../../common/hooks/useTranslation'
import { stripTagsAllowBasicHtml } from '../../../common/utils/xss'

const PriceBreakdown = styled(Text)<{ $isMobile: boolean }>`
  li {
    display: flex;
    flex-direction: ${(props) => (props.$isMobile ? 'column' : 'row')};
    justify-content: space-between;
    list-style: none;
    background-color: ${Gray100};
    border-top: 1px solid ${Gray200};
    border-bottom: 1px solid ${Gray200};
    padding: ${spacing[200].px};

    &:nth-of-type(even) {
      background-color: ${White};
      border-top: 1px solid ${White};
      border-bottom: 1px solid ${White};
    }
  }

  h2 {
    font-size: ${typography.baseTypography.size[400].rem};
    font-weight: ${typography.baseTypography.weight.Medium};
    margin-bottom: ${spacing[400].rem};
  }

  p {
    margin-bottom: ${spacing[400].rem};
  }

  strong {
    font-weight: ${typography.baseTypography.weight.Medium};
  }

  small {
    display: block;
    font-size: ${typography.baseTypography.size[100].rem};
    text-align: ${(props) => (props.$isMobile ? 'left' : 'right')};
  }
`

interface Props {
  price: AppointmentPrice
}

const AppointmentOptionsDentalPrice: React.FC<React.PropsWithChildren<Props>> = ({ price }) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const [priceBreakdownModalOpen, setPriceBreakdownModalOpen] = useState(false)

  if (!price.pricingInfo) {
    return null
  }

  return (
    <>
      <Stack direction={isMobile ? 'column' : 'row'} alignItems="flex-start" spacing={2}>
        <Stack direction="column" spacing={1}>
          <Text $size={100} $weight="Medium" $height="Large">
            Hinta-arvio
          </Text>
          <Text
            $size={100}
            $height="Large"
            dangerouslySetInnerHTML={{
              __html: stripTagsAllowBasicHtml(price.pricingInfo),
            }}
          />
        </Stack>
        {price.pricingInfoRows && (
          <Button
            onClick={() => setPriceBreakdownModalOpen(true)}
            variant="outlined"
            color="primary"
            size="small"
            sx={{ whiteSpace: 'nowrap' }}
          >
            {t('component.appointmentOptionsSelect.dentalPriceBreakdownButton')}
          </Button>
        )}
      </Stack>
      <PriceBreakdownModal
        open={priceBreakdownModalOpen}
        onClose={() => setPriceBreakdownModalOpen(false)}
        isMobile={isMobile}
        price={price}
        t={t}
      />
    </>
  )
}

interface ModalProps {
  open: boolean
  onClose: () => void
  isMobile: boolean
  price: AppointmentPrice
  t: TFunction
}

const PriceBreakdownModal: React.FC<React.PropsWithChildren<ModalProps>> = ({
  open,
  onClose,
  isMobile,
  price,
  t,
}) => {
  const headerRef = useRef<HTMLHeadingElement>(null)
  const handleEntered = () => {
    headerRef.current?.focus()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      fullWidth={true}
      disableAutoFocus={true}
      slotProps={{ transition: { onEntered: handleEntered } }}
    >
      <Stack direction="column" spacing={1}>
        <Text $height="Large" $weight="Medium" as="h2" ref={headerRef} tabIndex={-1}>
          {t('component.appointmentOptionsSelect.dentalPriceBreakdownModal.heading')}
        </Text>
        <PriceBreakdown
          $isMobile={isMobile}
          dangerouslySetInnerHTML={{
            __html: stripTagsAllowBasicHtml(price.pricingInfoRows ?? ''),
          }}
          aria-describedby="dental-price-breakdown-heading"
        />
      </Stack>
      <Text id="dental-price-breakdown-description">
        {t('component.appointmentOptionsSelect.dentalPriceBreakdownModal.description')}
      </Text>
    </Modal>
  )
}

export default AppointmentOptionsDentalPrice
